import { InferredMasterItem, OptimizePlanRequest, ProductionPlanService } from '@/api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable, DataTableExpandedRows, DataTableValueArray } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'
import { Tag } from 'primereact/tag'
import { useMemo, useState } from 'react'

interface PremiseCheckProps {
  optimizeRequest: OptimizePlanRequest
  onBack(): void
  onProceed(): void
}

const STATUS_OPTIONS = [
  { label: '推定のまま', value: '' },
  { label: '生産する', value: 'produce' },
  { label: '作らない', value: 'excluded' },
]

const RANK_OPTIONS = [
  { label: '推定のまま', value: '' },
  { label: 'A', value: 'A' },
  { label: 'B', value: 'B' },
  { label: 'C（注文数ちょうど）', value: 'C' },
]

const CONFIDENCE = {
  high: { label: '高', severity: 'success' },
  medium: { label: '中', severity: 'warning' },
  low: { label: '低', severity: 'danger' },
} as const

const PremiseCheck = ({ optimizeRequest, onBack, onProceed }: PremiseCheckProps) => {
  const start = optimizeRequest.days[0]?.date
  const end = optimizeRequest.days[optimizeRequest.days.length - 1]?.date
  const [onlyAttention, setOnlyAttention] = useState(true)
  const [keyword, setKeyword] = useState('')
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined)

  const query = useQuery({
    queryKey: ['inferredMaster', start, end],
    queryFn: () => ProductionPlanService.getInferredMaster(start, end),
    enabled: Boolean(start),
  })

  const save = useMutation({
    mutationFn: (args: { sku: string; field: 'status' | 'rank'; value: string }) =>
      ProductionPlanService.putInferredMasterOverride(
        args.value
          ? { sku: args.sku, [args.field]: args.value, clear: [] }
          : { sku: args.sku, clear: [args.field] },
      ),
    onSuccess: () => query.refetch(),
  })

  const items = useMemo(() => {
    const all = query.data?.items ?? []
    const kw = keyword.trim().toUpperCase()
    return all.filter(
      (it) =>
        (!onlyAttention || it.attention || it.overridden.length > 0) &&
        (!kw || it.sku.toUpperCase().includes(kw) || (it.name ?? '').toUpperCase().includes(kw)),
    )
  }, [query.data, onlyAttention, keyword])

  const attentionCount = (query.data?.items ?? []).filter((it) => it.attention).length
  const nothingToCheck = onlyAttention && !keyword.trim() && query.isSuccess && items.length === 0

  const statusBody = (it: InferredMasterItem) => (
    <div className="flex flex-column gap-1">
      <span className={it.overridden.includes('status') ? 'font-bold' : ''}>{it.statusLabel}</span>
      <Dropdown
        value={(it.override.status as string | undefined) ?? ''}
        options={STATUS_OPTIONS}
        optionValue="value"
        onChange={(e) => save.mutate({ sku: it.sku, field: 'status', value: e.value })}
        className="p-inputtext-sm"
        disabled={save.isPending}
      />
    </div>
  )

  const rankBody = (it: InferredMasterItem) => (
    <div className="flex flex-column gap-1">
      <span className={it.overridden.includes('rank') ? 'font-bold' : ''}>{it.rank ?? '―'}</span>
      <Dropdown
        value={(it.override.rank as string | undefined) ?? ''}
        options={RANK_OPTIONS}
        optionValue="value"
        onChange={(e) => save.mutate({ sku: it.sku, field: 'rank', value: e.value })}
        className="p-inputtext-sm"
        disabled={save.isPending}
      />
    </div>
  )

  const machineBody = (it: InferredMasterItem) => (
    <div className="text-sm">
      {Object.entries(it.machineShare).map(([m, share]) => (
        <div key={m}>
          {m} {Math.round(share * 100)}%
          {it.stdDailyQty[m] ? `・1日 ${it.stdDailyQty[m].toLocaleString('ja-JP')} 個` : ''}
        </div>
      ))}
      {Object.keys(it.machineShare).length === 0 && <span className="text-600">過去の実績なし</span>}
    </div>
  )

  const reasonBody = (it: InferredMasterItem) => (
    <div className="text-sm">
      {it.attention && <div className="text-orange-600 font-medium mb-1">{it.attention}</div>}
      <ul className="m-0 pl-3">
        {it.evidence.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  )

  // 行を開いたときだけ見せる推定の中身 (直せない参考情報)
  const detailBody = (it: InferredMasterItem) => {
    const c = CONFIDENCE[it.confidence as keyof typeof CONFIDENCE] ?? CONFIDENCE.medium
    return (
      <div className="flex flex-row flex-wrap gap-5 text-sm pl-5">
        <div>
          <div className="text-600 mb-1">機械（割合）・1日標準数</div>
          {machineBody(it)}
        </div>
        <div>
          <div className="text-600 mb-1">型のグループ</div>
          {it.moldGroup || '―'}
        </div>
        <div>
          <div className="text-600 mb-1">推定の信頼度</div>
          <Tag value={c.label} severity={c.severity} />
        </div>
      </div>
    )
  }

  return (
    <div className="mt-6">
      <h3 className="mt-0">作るかどうかの確認</h3>
      <p className="text-sm text-600">
        新方式（V7）は、成形一覧の注記（★外注・★削除など）とこれまでの計画から、品目ごとに作るかどうかを決めています。
        注文はあるのに作るかどうか迷う品目（これまで作っていない品目・新規品など）だけを下に表示します。
        違っていれば「生産する」「作らない」に変えてください。変えた内容は次回以降の計画にも使われます。
      </p>
      {query.isError && <Message severity="error" text="前提を読み込めませんでした" className="mb-3" />}
      {save.isError && <Message severity="error" text="保存できませんでした" className="mb-3" />}
      <div className="flex flex-row align-items-center gap-4 mb-3">
        <div className="flex align-items-center">
          <Checkbox inputId="only_attention" checked={onlyAttention} onChange={(e) => setOnlyAttention(Boolean(e.checked))} />
          <label htmlFor="only_attention" className="ml-2">
            確認が必要な品目と、変えた品目だけ表示（{attentionCount} 品目）
          </label>
        </div>
        <InputText
          placeholder="品番で絞り込み"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="p-inputtext-sm"
        />
      </div>
      {nothingToCheck ? (
        <Message
          severity="success"
          text="確認が必要な品目はありません。このまま最適化へ進めます。"
          className="w-full justify-content-start"
        />
      ) : (
        <DataTable
          value={items}
          loading={query.isLoading}
          size="small"
          dataKey="sku"
          paginator
          rows={15}
          emptyMessage="該当する品目はありません"
          rowClassName={(it: InferredMasterItem) => (it.attention ? 'bg-yellow-50' : '')}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={detailBody}
        >
          <Column expander style={{ width: '3rem' }} />
          <Column
            header="品目"
            body={(it: InferredMasterItem) => (
              <div>
                <div className="font-medium">{it.sku}</div>
                {it.demandInPeriod > 0 && (
                  <div className="text-xs text-600">期間の注文 {it.demandInPeriod.toLocaleString('ja-JP')} 個</div>
                )}
              </div>
            )}
          />
          <Column header="作るかどうか" body={statusBody} style={{ minWidth: '11rem' }} />
          <Column header="ランク" body={rankBody} style={{ minWidth: '8rem' }} />
          <Column header="理由" body={reasonBody} style={{ minWidth: '20rem' }} />
        </DataTable>
      )}
      <div className="flex flex-row justify-content-between mt-6">
        <Button label="戻る" icon="pi pi-arrow-left" iconPos="left" size="small" severity="secondary" onClick={onBack} />
        <Button label="最適化へ" icon="pi pi-arrow-right" iconPos="right" severity="success" onClick={onProceed} />
      </div>
    </div>
  )
}

export default PremiseCheck
